import { registerBlock } from "../registry";
import { makeId } from "../helpers";
import type { ContentBlock, ContentTag } from "../types";

/**
 * Donor-facing education belongs in its own article instead of sharing the
 * intended-parent donor-gamete overview. The two audiences have different
 * decisions, risks, and questions to take to a clinic.
 */
registerBlock({
  id: makeId("moon-article-egg-donation-guide"),
  kind: "article",
  tags: [
    "moon",
    "moon-guides",
    "her",
    "pre-cycle",
    "clinic-visit",
    "practical",
    "emotional",
    "recovery",
    "evidence-based",
  ] as ContentTag[],
  title: "Egg donation: a guide for donors",
  subtitle:
    "What screening, stimulation, retrieval, recovery, and life after donation can look like.",
  readTimeMinutes: 12,
  lastReviewed: "2026-07-18",
  body: {
    introduction:
      "Choosing to donate eggs can be generous and meaningful, and it is also a real medical process. This guide explains the usual path so you can make an informed decision, ask better questions, and recognize when you need help. Every clinic, agency, and country has its own requirements and consent documents; your care team’s instructions take priority.",
    quickSummary: [
      {
        title: "It is more than one appointment.",
        description:
          "Donation usually includes an application, medical and psychological screening, daily medication, several monitoring visits, an egg retrieval, and recovery time.",
      },
      {
        title: "Screening protects everyone involved.",
        description:
          "Clinics may review your health and family history and perform infectious-disease, genetic, laboratory, ultrasound, and psychological assessments before accepting you.",
      },
      {
        title: "Your consent and safety come first.",
        description:
          "Ask for the full plan in writing, including risks, emergency contacts, expenses, compensation, privacy, future contact, and how to pause or withdraw before the consent deadline.",
      },
    ],
    sections: [
      {
        title: "What egg donation involves",
        content:
          "In an egg donation cycle, a clinic uses medication to help your ovaries mature multiple follicles. The eggs are collected during a short outpatient procedure and may be fertilized for an intended parent or parents. You are not carrying the pregnancy, and you do not have to guarantee a particular number of eggs or a pregnancy outcome. The clinic or agency should explain who coordinates your care, who pays for each part of the process, and what information can be shared with you or the recipient.",
        bulletPoints: [
          "An application or referral is followed by eligibility screening",
          "Medication stimulates several follicles to grow during one cycle",
          "Monitoring visits guide medication changes and retrieval timing",
          "Eggs are collected under sedation or anesthesia at the clinic",
          "Donation does not guarantee fertilization, pregnancy, or a live birth",
        ],
      },
      {
        title: "Before you apply: questions to ask yourself",
        content:
          "There is no single right reason to donate. Before you begin, think about whether you have enough time and support for appointments, injections, a procedure, and recovery. Consider how you feel about genetic connection, privacy, DNA testing, possible future contact, and updates about the eggs or children conceived from them. You should be able to make this decision without pressure from a partner, family member, agency, clinic, or payment. A reputable program gives you space to ask questions and say no.",
        bulletPoints: [
          "Can I attend monitoring visits and arrange a safe ride home after retrieval?",
          "Do I have support if I feel uncomfortable, anxious, or unwell?",
          "Have I understood the medical, emotional, legal, and privacy implications?",
          "Do I know which decisions can still be changed and when consent becomes final?",
          "Would I feel comfortable pausing if new information changes my mind?",
        ],
      },
      {
        title: "Screening and matching",
        content:
          "Screening is designed to assess your health and support informed consent, not to judge your worth. Depending on the program, it may include a detailed medical and family history, physical examination, blood tests, infectious-disease testing, genetic or carrier screening, ovarian-reserve testing, ultrasound, and a meeting with a mental-health professional. Some programs also complete legal, identity, or background steps. Eligibility rules differ, and not every applicant is accepted. Ask which results are shared, how records are protected, and whether you will have a chance to review concerns with an independent professional.",
        bulletPoints: [
          "Medical history, medications, allergies, and family health history",
          "Blood work, infectious-disease testing, and genetic or carrier screening",
          "Ultrasound and other tests of ovarian response or reserve",
          "Psychological counseling or evaluation focused on informed consent",
          "Written information about privacy, legal status, and the matching process",
        ],
      },
      {
        title: "The stimulation cycle",
        content:
          "Once you are cleared and matched, you will usually give yourself daily fertility medications for roughly one to two weeks, although the exact schedule varies. The medicines encourage multiple follicles to develop in the same cycle. Your clinic may add another medication to prevent ovulation before retrieval. Learn the injection technique, storage instructions, missed-dose plan, and after-hours number before you start. Do not change a dose or skip a medication on your own. Follow your clinic’s instructions about sex, exercise, travel, alcohol, and other activities, because the ovaries can become enlarged and more sensitive during stimulation.",
        bulletPoints: [
          "Ask for a written medication calendar and a demonstration before the first dose",
          "Use alarms or a support person if timing several medications feels difficult",
          "Keep the clinic’s after-hours number where you can find it",
          "Report worsening pain, rapid swelling, or other concerning symptoms promptly",
          "Follow the clinic’s activity and pregnancy-prevention instructions",
        ],
      },
      {
        title: "Monitoring and the trigger",
        content:
          "Monitoring appointments commonly combine an ultrasound with blood tests. They help the team see how your follicles are growing and decide whether medication or timing should change. As the follicles mature, visits may become more frequent. When the team decides the eggs are ready, you will be given a trigger medication at a precise time. Retrieval is often scheduled about a day and a half later, but your clinic’s timing is the one to follow. If you miss the trigger window or cannot get a medication, call the clinic immediately rather than trying to solve it yourself.",
        bulletPoints: [
          "Bring your medication list and report every new symptom at monitoring visits",
          "Ask what the ultrasound and blood-test results mean for your plan",
          "Write down the exact trigger time and set more than one reminder",
          "Call right away if a dose is late, missed, spilled, or stored incorrectly",
        ],
      },
      {
        title: "Egg retrieval",
        content:
          "Egg retrieval is usually an outpatient procedure performed with ultrasound guidance. After sedation or anesthesia, the clinician uses a thin needle through the vaginal wall to collect fluid from the follicles. The procedure itself is often brief, but the appointment includes preparation and recovery. You will need an approved adult to take you home and should plan for a quiet rest of the day. The number of eggs collected can be different from the number of follicles seen, and the final outcome is not something a donor can control.",
        bulletPoints: [
          "Confirm fasting, medication, arrival, and transportation instructions beforehand",
          "Arrange a responsible adult to drive you home and stay available afterward",
          "Expect short-term cramping, bloating, fatigue, or light spotting for some people",
          "Ask when the clinic will contact you about the number of eggs collected",
          "Follow the written restrictions for exercise, sex, work, and bathing",
        ],
      },
      {
        title: "Side effects and warning signs",
        content:
          "Mild bloating, pelvic pressure, cramps, breast tenderness, fatigue, mood changes, or injection-site soreness can occur during stimulation or after retrieval. More serious complications are uncommon, but ovarian hyperstimulation syndrome (OHSS) can happen when the ovaries respond strongly to medication. Contact your clinic urgently for rapidly worsening abdominal swelling or pain, repeated vomiting, shortness of breath, fainting, a sudden jump in weight, or much less urine than usual. Fever, heavy bleeding, severe or one-sided pain, chest pain, or any symptom that feels alarming also warrants prompt medical advice. If you cannot reach the care team and symptoms are severe, use emergency services.",
        bulletPoints: [
          "Call the clinic for symptoms that are worsening instead of improving",
          "Ask whether and how often they want you to track weight, fluids, or urine",
          "Seek urgent help for trouble breathing, chest pain, fainting, or severe pain",
          "Do not wait for the next scheduled appointment when a warning sign appears",
        ],
      },
      {
        title: "Recovery and future fertility",
        content:
          "Recovery varies. Some donors return to usual routines quickly, while others need several days or longer before they feel fully comfortable. Keep your follow-up appointment and follow the clinic’s instructions about activity and medications. Current evidence has not demonstrated a meaningful change in ovarian reserve from donation, but long-term data—especially after repeated cycles—remain limited. Repeated donation also adds cumulative exposure to procedures and medication. Programs set their own limits; ASRM supports a prudent maximum of six stimulated retrieval cycles. Donation is not a guarantee about your future fertility, so ask your clinician how your personal health and plans affect the decision.",
        bulletPoints: [
          "Plan a low-demand day after retrieval and backup time if recovery takes longer",
          "Attend follow-up and report symptoms that persist or worsen",
          "Ask how the program tracks your prior cycles and cumulative risk",
          "Discuss your own future fertility goals with an independent clinician",
          "Confirm the program’s cycle limit and what happens if you stop early",
        ],
      },
      {
        title: "Consent, identity, and what happens later",
        content:
          "Donation arrangements can be known, directed, or arranged through a bank or agency, and the terms vary by program and jurisdiction. Ask what information the recipient may receive, whether medical updates can be requested, and whether future contact is possible. The word anonymous is no longer a guarantee: consumer DNA testing, social media, and changing program policies can make identity discovery possible even when an agreement says contact is not expected. Read every consent and legal document carefully. You should understand what happens to unused eggs or embryos, whether you can withdraw and until when, and how future medical information will be updated.",
        bulletPoints: [
          "Ask what identifying and non-identifying information is shared now",
          "Discuss DNA testing, identity discovery, and possible future contact",
          "Understand who can request or update relevant medical information",
          "Read the withdrawal deadline and rules for unused eggs or embryos",
          "Consider independent legal advice before signing donor agreements",
        ],
      },
      {
        title: "Compensation and practical planning",
        content:
          "Compensation, reimbursement, and eligibility rules differ by country, clinic, and agency. A responsible program should explain in writing how compensation reflects your time, inconvenience, discomfort, screening, medication, retrieval, and recovery—not promise payment in exchange for a particular egg count or outcome. Ask when you are paid, which costs are covered, and what happens if a cycle is canceled or you become medically unable to continue. Also plan for transportation, time away from work or school, childcare, meals, taxes, and any insurance questions. Money can be part of the decision, but it should never replace understanding the risks or having the freedom to say no.",
        bulletPoints: [
          "Request a written compensation and expense schedule before consenting",
          "Ask who pays for screening, medication, monitoring, retrieval, and treatment of complications",
          "Clarify payment if the cycle is canceled, paused, or yields fewer eggs",
          "Plan for travel, work, school, childcare, meals, and recovery time",
          "Ask a tax professional or legal adviser about questions specific to you",
        ],
      },
      {
        title: "Questions to take to a clinic or agency",
        content:
          "You do not need to remember everything during one consultation. Save these questions, bring a trusted person if you want, and ask for answers in writing. You can compare programs before deciding.",
        bulletPoints: [
          "What are your eligibility rules, and which tests or records do you require?",
          "Who is my medical team, and who do I call nights and weekends?",
          "What medications, monitoring visits, trigger, sedation, and retrieval should I expect?",
          "What are the clinic’s plans for preventing and treating OHSS or another complication?",
          "How many cycles do you allow, and how do you track cumulative exposure?",
          "What compensation, reimbursement, cancellation, tax, and insurance terms apply?",
          "What are the privacy, DNA-testing, identity-release, and future-contact policies?",
          "When does consent become final, and what happens to unused eggs or embryos?",
          "Can I speak with an independent fertility counselor or reproductive attorney?",
        ],
      },
      {
        title: "You can take your time",
        content:
          "A good clinic or agency will make room for informed questions, independent support, and a decision that is genuinely yours. If the answers are unclear, the timeline feels rushed, or you feel pressured to continue, pause and ask for help. You can compare providers and revisit the decision when you have the information and support you need.",
      },
    ],
    references: [
      {
        title: "Guidance regarding gamete and embryo donation (2024)",
        authors:
          "Practice Committee of the American Society for Reproductive Medicine",
        journal: "Fertility and Sterility",
        year: 2024,
        url: "https://www.asrm.org/practice-guidance/practice-committee-documents/guidance-regarding-gamete-and-embryo-donation/",
        evidenceLevel: "guideline",
      },
      {
        title: "Repetitive oocyte donation: a committee opinion (2020)",
        authors:
          "Practice Committee of the American Society for Reproductive Medicine",
        journal: "Fertility and Sterility",
        year: 2020,
        url: "https://www.asrm.org/practice-guidance/practice-committee-documents/repetitive-oocyte-donation-a-committee-opinion-2020/",
        evidenceLevel: "guideline",
      },
      {
        title:
          "Financial compensation of oocyte donors: an Ethics Committee opinion (2021)",
        authors:
          "Ethics Committee of the American Society for Reproductive Medicine",
        journal: "Fertility and Sterility",
        year: 2021,
        url: "https://www.asrm.org/practice-guidance/ethics-opinions/financial-compensation-of-oocyte-donors-an-ethics-committee-opinion/",
        evidenceLevel: "guideline",
      },
      {
        title:
          "Testing donors of human cells, tissues, and cellular and tissue-based products",
        authors: "U.S. Food and Drug Administration",
        url: "https://www.fda.gov/vaccines-blood-biologics/safety-availability-biologics/testing-donors-human-cells-tissues-and-cellular-and-tissue-based-products-hctp-specific-requirements",
        evidenceLevel: "official",
      },
      {
        title: "Donating your eggs",
        authors: "Human Fertilisation and Embryology Authority",
        url: "https://www.hfea.gov.uk/donation/donors/donating-your-eggs/",
        evidenceLevel: "official",
      },
    ],
    relatedArticles: [
      makeId("moon-article-egg-freezing-timeline"),
      makeId("moon-article-cycle-process"),
      makeId("new-article-donor-guide"),
    ],
  },
} satisfies ContentBlock<"article">);
